import { PLATFORM_PERMISSION_DENIED, PLATFORM_SESSION_QUERY_KEY } from './permissions';
import { queryClient } from './queryClient';
import { endSession, getAccessToken } from './session';

const API_BASE = (import.meta.env.VITE_API_URL as string) ?? '';

function getToken(): string {
  return getAccessToken() ?? '';
}

interface GraphQlErrorOriginal {
  code?: string;
  field?: string;
  message?: string | string[];
}

interface GraphQlResponse<T> {
  data?: T;
  errors?: {
    message: string;
    extensions?: { code?: string; originalError?: GraphQlErrorOriginal };
  }[];
}

/**
 * OS-1712 — error de negocio del API con su código funcional (`SUBDOMAIN_TAKEN`, `NIT_INVALID`…)
 * y el campo afectado, para llevar al usuario al paso o campo que debe corregir.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly field?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function toApiError(error: NonNullable<GraphQlResponse<unknown>['errors']>[number]): ApiError {
  const original = error.extensions?.originalError;
  // Errores de class-validator: `message` es una lista; se muestra la primera.
  const detail = Array.isArray(original?.message) ? original.message[0] : undefined;
  return new ApiError(
    detail ?? error.message ?? 'GraphQL error',
    original?.code ?? error.extensions?.code,
    original?.field,
  );
}

export interface PlatformRequestOptions {
  /**
   * OS-1710 — un 401 cierra la sesión y lleva a `/login?expired=1`. Los endpoints de login y
   * verify-mfa lo desactivan: ahí un 401 es "credenciales/código inválido", no sesión vencida.
   */
  redirectOnUnauthorized?: boolean;
}

/** Error de sesión: la petición falló porque la sesión de plataforma ya no es válida. */
export class SessionExpiredError extends Error {
  constructor() {
    super('Tu sesión expiró. Inicia sesión de nuevo.');
    this.name = 'SessionExpiredError';
  }
}

/**
 * OS-1709 — 429: demasiados intentos (rate limit o cuenta bloqueada). Nunca es una sesión
 * expirada: no cierra la sesión ni redirige; se muestra el mensaje del servidor.
 */
export class RateLimitedError extends Error {
  constructor(message?: string) {
    super(message || 'Demasiados intentos. Espera unos minutos e intenta de nuevo.');
    this.name = 'RateLimitedError';
  }
}

async function messageFrom(res: Response): Promise<string | undefined> {
  const body: unknown = await res.json().catch(() => ({}));
  return (body as { message?: string }).message;
}

function handleUnauthorized(redirect: boolean): void {
  if (redirect) {
    endSession('expired');
  }
}

export async function gql<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${API_BASE}/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (res.status === 401) {
    handleUnauthorized(true);
    throw new SessionExpiredError();
  }
  if (res.status === 429) {
    throw new RateLimitedError(await messageFrom(res));
  }
  if (!res.ok) {throw new Error(`HTTP ${res.status}`);}

  const json = (await res.json()) as GraphQlResponse<T>;
  // Apollo responde 200 con `extensions.code = UNAUTHENTICATED` cuando un guard rechaza el token.
  if (json.errors?.some((error) => error.extensions?.code === 'UNAUTHENTICATED')) {
    handleUnauthorized(true);
    throw new SessionExpiredError();
  }
  const firstError = json.errors?.[0];
  if (firstError) {
    const error = toApiError(firstError);
    if (error.code === PLATFORM_PERMISSION_DENIED) {
      // OS-1717 — el rol pudo cambiar en otra sesión: se relee para ajustar menús y acciones.
      void queryClient.invalidateQueries({ queryKey: PLATFORM_SESSION_QUERY_KEY });
    }
    throw error;
  }
  if (!json.data) {throw new Error('No data');}

  return json.data;
}

async function parseRestResponse<T>(res: Response, redirect: boolean): Promise<T> {
  if (res.status === 401 && redirect) {
    handleUnauthorized(true);
    throw new SessionExpiredError();
  }
  if (res.status === 429) {
    throw new RateLimitedError(await messageFrom(res));
  }
  if (res.status === 403) {
    // OS-1717 — sin la capacidad: el rol pudo cambiar, se relee la sesión (no se cierra).
    void queryClient.invalidateQueries({ queryKey: PLATFORM_SESSION_QUERY_KEY });
  }

  if (!res.ok) {
    throw new Error((await messageFrom(res)) ?? `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

export async function platformFetch<T>(
  path: string,
  options: RequestInit = {},
  { redirectOnUnauthorized = true }: PlatformRequestOptions = {},
): Promise<T> {
  // NestJS aplica setGlobalPrefix('api') a todos los REST controllers (ver apps/api/src/main.ts) —
  // GraphQL queda fuera (Apollo monta en /graphql sin prefijo), por eso solo esta función lo agrega.
  const res = await fetch(`${API_BASE}/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers ?? {}),
    },
  });

  return parseRestResponse<T>(res, redirectOnUnauthorized);
}

/** Upload multipart (logo/banner) — no fuerza Content-Type JSON. */
export async function platformUpload<T>(path: string, formData: FormData): Promise<T> {
  const res = await fetch(`${API_BASE}/api${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
    body: formData,
  });

  return parseRestResponse<T>(res, true);
}
